import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import InfoIcon from '@mui/icons-material/Info';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Box, Divider, IconButton, LinearProgress, ListItemIcon, ListItemText, Menu, MenuItem, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import Pagination from '@mui/material/Pagination';
import { AddUserDialog } from 'Components/SettingsTs/AddUserDialog';
import { AccessLevelBullets, AccessLevelsTableHeads, RoleAccessMap } from 'Components/SettingsTs/SettingsConstants';
import { AccessLevelTableHead, IAccessLevelsRow, IAccount } from 'Components/SettingsTs/SettingsModel';
import { ConfirmationDialog } from 'Components/SharedComponents/ConfirmationDialog/ConfirmationDialog';
import { IConfirmationDialogLabels } from 'Components/SharedComponents/ConfirmationDialog/ConfirmationDialogModel';
import { ShTooltip } from 'Components/SharedComponents/ShTooltip';
import { useAppDispatch, useAppSelector } from 'helpers/hooks';
import { MouseEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShAlert } from 'shared/SharedStyles/ShFeedback';
import { ShButton } from 'shared/SharedStyles/ShInputs';
import { PaperVariant } from 'shared/SharedStyles/styleConstants';
import { ApiStates } from 'shared/constants';
import { deleteUser, getAccountList, getRoles, resetAddUser, resetDeleteUser, resetUpdateUserRoles } from 'store/slices/employer/settingsTs/access-levels';

// Table cell component to handle different type of table data
const Cell = ({ headerId, row }: { headerId: AccessLevelTableHead, row: IAccount }) => {
    switch (headerId) {
        case 'NAME':
            return <TableCell>{row.account?.first_name + ' ' + (row.account?.middle_name ? row.account?.middle_name + ' ' : ' ') + row.account?.last_name ?? ''}</TableCell>;
        case 'EMAIL':
            return <TableCell>{row.account?.email}</TableCell>
        case 'ROLE':
            return <TableCell>
                <Stack className='role-cell'>{row.role?.name}&nbsp;<HelpOutlineIcon color='disabled' /></Stack>
            </TableCell>;
        case 'ASSIGN':
        case 'COMMENT':
        case 'CREATE':
        case 'VIEW':
        case 'ALL_JOBS':
            return <TableCell align='center'>{RoleAccessMap[row.account.role_id]?.includes(headerId) ?
                <CheckIcon fontSize='small' color='success' /> : <ClearIcon fontSize='small' color='error' />}</TableCell>;
        default:
            break;
    }
    return (<></>);
};

// Row component to wrap table row.
const Row = ({ row, editUser, rolesList }: IAccessLevelsRow) => {
    const dispatch = useAppDispatch();
    /*
       Using map with Job id as key and element ref as value since the menu's ref is present in multiple locations opening one
       menu toggles multiple menus to open. With the help of this map toggling menu for appropriate element is possible.
       */
    const [moreActionsAnchorEl, setMoreActionsAnchorEl] = useState<{ [key: number]: HTMLElement | null }>({ 0: null });

    /*
       User delete confirmation dialog.
    */
    const [confirmDialogLabels, setConfirmDialogLabels] = useState<IConfirmationDialogLabels | undefined>(undefined);
    const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState<boolean>(false);
    const [userIdToBeDeleted, setUserIdToBeDeleted] = useState<number | undefined>(undefined);

    const openActions = (e: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>, indx: number) => {
        e.stopPropagation();
        setMoreActionsAnchorEl({ ...moreActionsAnchorEl, [indx]: e.currentTarget });
    };

    const deleteUserAccount = (user: IAccount) => {
        // Set confirmation dialog's labels, appropriately with user name etc.
        setConfirmDialogLabels({
            title: 'Confirm User Deletion',
            contentText: `Please confirm that you want to delete ${user.account.first_name}'s account. \nThis action is irreversible.`
        });
        // Set userIdToBeDeleted which will be passed to delete API if user confirm's the action.
        setUserIdToBeDeleted(user.id);
        setIsConfirmDialogOpen(true);
    };

    const cancelDeleteUser = () => {
        setIsConfirmDialogOpen(false);
        setUserIdToBeDeleted(undefined);
        setConfirmDialogLabels({ ...confirmDialogLabels, contentText: '' });
    };

    return (
        <>
            <TableRow key={row.account?.email}>
                {AccessLevelsTableHeads.map(th => (
                    <Cell key={th.columName} headerId={th.columName} row={row} />
                ))}
                <TableCell align='center'>
                    {row.role_id !== rolesList.find(r => r.name === 'owner')?.id && <>
                        <Tooltip title="More Actions" placement='left' onClick={e => e.stopPropagation()}>
                            <IconButton className='more-btn' aria-label='More options' onClick={e => { openActions(e, row.id) }}>
                                <MoreVertIcon />
                            </IconButton>
                        </Tooltip>
                        {moreActionsAnchorEl[row.id] && <Menu id="more-options" aria-label='More options menu' anchorEl={moreActionsAnchorEl[row.id]}
                            open={moreActionsAnchorEl[row.id] !== null} onClose={() => setMoreActionsAnchorEl({ ...moreActionsAnchorEl, [row.id]: null })}
                            onClick={e => { setMoreActionsAnchorEl({ ...moreActionsAnchorEl, [row.id]: null }); e.stopPropagation() }}>
                            <MenuItem onClick={() => editUser(row)}>
                                <ListItemIcon><EditIcon color='primary' /></ListItemIcon>
                                <ListItemText>Edit User</ListItemText>
                            </MenuItem>
                            <MenuItem onClick={() => deleteUserAccount(row)}>
                                <ListItemIcon><DeleteIcon color='error' /></ListItemIcon>
                                <ListItemText>Delete User</ListItemText>
                            </MenuItem>
                        </Menu>}
                    </>}
                </TableCell>
                {/* ConfirmationDialog will be rendered only is 'userIdToBeDeleted' is present. */}
                {userIdToBeDeleted &&
                    <ConfirmationDialog contentText={confirmDialogLabels?.contentText} title={confirmDialogLabels?.title}
                        isDialogOpen={isConfirmDialogOpen} onCancel={cancelDeleteUser}
                        onConfirm={() => {
                            dispatch(resetUpdateUserRoles());
                            dispatch(deleteUser(userIdToBeDeleted));
                        }} />}
            </TableRow>
        </>
    );
}

export const AccessLevels = () => {
    const dispatch = useAppDispatch();
    const { employerId, accountAccess } = useAppSelector((state) => state.auth.auth);
    const { getAccountListApiStatus, getAccountListApiResponse, accountList, addUserApiStatus, updateRolesApiStatus, rolesList,
        getRolesListApiStatus, getRolesListApiResponse, updateRolesApiResponse, deleteUserApiResponse,
        deleteUserApiStatus
    } = useAppSelector((state) => state.employer.accessLevels);
    const [isAddUserDialogOpen, setIsAddUserDialogOpen] = useState<boolean>(false);
    const [componentAccountList, setComponentAccountList] = useState<IAccount[]>([]);
    const [existingUser, setExistingUser] = useState<IAccount | undefined>(undefined);

    const [currentPage, setCurrentPage] = useState<number>(1);
    const [paginatedAccountList, setPaginatedAccountList] = useState<IAccount[]>([]);
    const navigate = useNavigate();

    const handlePageChange = (newPage: number) => {
        setCurrentPage(newPage);
    };

    useEffect(() => {
        // Update the data for pagination
        const startIndex = (currentPage - 1) * 10;
        const endIndex = startIndex + 10;
        setPaginatedAccountList(componentAccountList.slice(startIndex, endIndex));
    }, [componentAccountList, currentPage]);


    useEffect(() => {
        dispatch(getRoles());
        return () => {
            // Reset update company info, logoAPIvariables on component unmount.
            dispatch(resetUpdateUserRoles());
        }
    }, [dispatch]);

    // load account list.
    useEffect(() => {
        dispatch(getAccountList());
    }, [dispatch]);

    // re-load account list when role is updated.
    useEffect(() => {
        if (updateRolesApiStatus === 'success') {
            dispatch(getAccountList());
        }
    }, [dispatch, updateRolesApiStatus]);

    // set account list.
    useEffect(() => {
        setComponentAccountList(accountList);
    }, [accountList, dispatch]);

    // Edit user handler which gets invoked from Row component.
    const editUser = (user: IAccount) => {
        // Update the 'existingUser' variable with the User's data that's going to be edited.
        setExistingUser({ ...user });
        setIsAddUserDialogOpen(true);
    };

    // Update table data if user is successfully added.
    useEffect(() => {
        if (addUserApiStatus === ApiStates.success) {
            dispatch(getAccountList());
        }
    }, [addUserApiStatus, dispatch]);

    // Update table data if user is successfully removed.
    useEffect(() => {
        if (deleteUserApiStatus === ApiStates.success) {
            dispatch(getAccountList());
        }
    }, [deleteUserApiStatus, dispatch]);


    const AlertForRolesUpdate = () => {
        switch (updateRolesApiStatus) {
            case ApiStates.pending:
                return (<ShAlert severity="info">Updating Roles...</ShAlert>);
            case ApiStates.success:
                return (<ShAlert severity="success" onClose={() => dispatch(resetUpdateUserRoles())}>
                    <Typography fontWeight={600}>{updateRolesApiResponse}</Typography>
                </ShAlert>);
            case ApiStates.failed:
                return (<ShAlert severity="error">{updateRolesApiResponse}</ShAlert>);
            default:
                return (<></>);
        }
    };

    const AlertForUserDelete = () => {
        switch (deleteUserApiStatus) {
            case ApiStates.pending:
                return (<ShAlert severity="info">Deleting User...</ShAlert>);
            case ApiStates.success:
                return (<ShAlert severity="success" onClose={() => dispatch(resetDeleteUser())}>
                    <Typography fontWeight={600}>{deleteUserApiResponse}</Typography>
                </ShAlert>);
            case ApiStates.failed:
                return (<ShAlert severity="error">{deleteUserApiResponse}</ShAlert>);
            default:
                return (<></>);
        }
    };

    const closeAddUserDialog = () => {
        setIsAddUserDialogOpen(false);
        /*
            If 'existingUser' is not undefined i.e., closeAddUserDialog is called after a user edit, set existingUser to undefined.
        */
        if (existingUser) {
            setExistingUser(undefined);
        }
    };

    // Reset add user and update user state on component unmount.
    useEffect(() => {
        return () => {
            dispatch(resetUpdateUserRoles());
            dispatch(resetAddUser());
            dispatch(resetDeleteUser());
        };
    }, [dispatch]);

    useEffect(() => {
        if (accountAccess?.role !== 'admin' && accountAccess?.role !== 'owner') {
            navigate("/employer/dashboard");
        }
    }, [accountAccess?.role, navigate])

    return (
        <>
            <Stack minHeight='350px'>
                {/* Alerts for account and roles listAPIerrors */}
                {getAccountListApiStatus === ApiStates.failed && <ShAlert severity="error">
                    {getAccountListApiResponse ?? 'Error occurred while fetching Account List'}
                </ShAlert>}
                {getRolesListApiStatus === ApiStates.failed && <ShAlert severity="error">
                    {getRolesListApiResponse ?? 'Error occurred while fetching Roles List'}
                </ShAlert>}
                <Typography component={'span'} variant='body2'>
                    <Stack columnGap={1} flexDirection='row' alignItems='center'>
                        Be the President of your account by using your owner status to
                        add more people and assign their access levels.<ShTooltip
                            title={<ul>{AccessLevelBullets.map(aLB => (<li key={aLB}>{aLB}</li>))}</ul>}
                            placement="right"><InfoIcon fontSize="small" color='disabled' /></ShTooltip></Stack>
                </Typography>
                <Stack marginTop={2} marginBottom={2} flexDirection='row' justifyContent='flex-end'>
                    {accountAccess?.role !== 'admin' &&
                        <ShButton disableElevation variant='contained' onClick={() => setIsAddUserDialogOpen(true)}>
                            <Typography variant='body2'>Add Users</Typography>
                        </ShButton>}
                    {/* Add User Dialog */}
                    {employerId &&
                        <AddUserDialog accountList={accountList} closeAddUserDialog={closeAddUserDialog}
                            employerId={employerId} componentAccountList={componentAccountList}
                            isAddUserDialogOpen={isAddUserDialogOpen} user={existingUser} />
                    }
                </Stack>
                {getAccountListApiStatus === 'pending' ? <LinearProgress /> : <>
                    {componentAccountList?.length > 0 ?
                        <>
                            {updateRolesApiStatus !== 'idle' && <Box marginBottom={2}>
                                {AlertForRolesUpdate()}
                            </Box>}
                            {deleteUserApiStatus !== 'idle' &&
                                <Box marginBottom={2}>
                                    {AlertForUserDelete()}
                                </Box>
                            }
                            <Paper variant={PaperVariant}>
                                <TableContainer className='acc-lvl-tbl-cntnr'>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                {AccessLevelsTableHeads.map(th => (
                                                    <TableCell key={th.columName} align={th.textAlign}>
                                                        <Typography variant='body2' fontWeight={600}>{th.label}</Typography>
                                                    </TableCell>
                                                ))}
                                                <TableCell align='center'>
                                                    <Typography variant='body2' fontWeight={600}>Actions</Typography>
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {paginatedAccountList?.map((acc) => (
                                                <Row key={acc.id} row={acc} editUser={editUser} rolesList={rolesList} />
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>

                                {/* Display Pagination Controls only if there are more than 10 records*/}
                                {Math.ceil(componentAccountList.length / 10) > 1 && <>
                                    <Divider />
                                    <Stack alignItems='flex-end' padding={1}>
                                        <Pagination
                                            count={Math.ceil(componentAccountList.length / 10)}
                                            page={currentPage}
                                            onChange={(event, value) => handlePageChange(value)}
                                            color="primary"
                                        />
                                    </Stack>
                                </>}
                            </Paper>
                        </>
                        : getAccountListApiStatus === 'success' && <>No accounts found</>}
                </>}
            </Stack>
        </>
    );
}
